import React, { FC, ReactNode, isValidElement, ChangeEvent, useState, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';

import { connect } from 'react-redux';
import Text, { TextProps } from '../Text';

import styles from './index.pcss';
import properties from '../../../modules/ajio-util/properties';
import ProgressLabel from '../ProgressLabel';
import RateCalculationPopup from '../../organism/RateCalculationPopup';
import { getSubratingFlag, getShowRateCalculate, getRatingsColorConfig, getRatingParityMsiteFlag } from '../../selectors/featureFlagsSelector';
import { getProductItemPDP, segmentEventPush } from '../../../modules/ga/GTMdatalayer';

export type Id = string | number;
export type Size = 'sm' | 'md';
export type LabelType = string | ReactNode;

interface RatingCardProps {
  totalRatingCount: string;
  totalStarCount: string;
  image: string;
  rateDesign: string;
  isProgressBar: boolean;
  rateData: object;
  customerWords: any;
  isFleek: boolean;
  ratingParityMsite: boolean;
}

const mapStateToProps = state => {
  const ratingQuestionsMapping = getSubratingFlag(state?.flags);
  const showRateCalculate = getShowRateCalculate(state?.flags);

  return {
    ratingQuestionsMapping,
    showRateCalculate,
    ratingsColor: getRatingsColorConfig(state?.flags),
    ratingParityMsite: getRatingParityMsiteFlag(state)
  };
};
const connector = connect(mapStateToProps);
const RatingCard: FC<RatingCardProps> = (props: RatingCardProps) => {
  const {
    totalRatingCount,
    totalStarCount,
    image,
    rateDesign,
    isProgressBar,
    rateData,
    isDesktop,
    customerWords,
    ratingQuestionsMapping,
    showRateCalculate,
    isFleek = false,
    ratingsColor = {},
    productData={},
    ratingParityMsite = false
  } = props;

  const { thresholdValue = 3 } = ratingsColor;

  const [showCalculatePopup, setShowCalculatePopup] = useState(false);
  const imageBaseUrl: string = properties.getProperty('imageBaseUrl');
  const starUrl = image
    ? `${imageBaseUrl}/static/img/${image}`
    : `${imageBaseUrl}/static/img/grren_star_pdp.svg`;
  const starAltText = image ? '' : 'Star selected';
  const redStarUrl = `${imageBaseUrl}/static/img/red-star.svg`;
  const cx = classNames.bind(styles);
  const ratingRef = useRef(null);

  const calculatePopup = () => {
    setShowCalculatePopup(!showCalculatePopup);
  };
  const stars = [
    {
      id: 1,
      star: '5',
      percent: rateData?.percentage5StarRatings,
      numStar: rateData?.numUser5StarRatings
    },
    {
      id: 2,
      star: '4',
      percent: rateData?.percentage4StarRatings,
      numStar: rateData?.numUser4StarRatings
    },
    {
      id: 3,
      star: '3',
      percent: rateData?.percentage3StarRatings,
      numStar: rateData?.numUser3StarRatings
    },
    {
      id: 4,
      star: '2',
      percent: rateData?.percentage2StarRatings,
      numStar: rateData?.numUser2StarRatings
    },
    {
      id: 5,
      star: '1',
      percent: rateData?.percentage1StarRatings,
      numStar: rateData?.numUser1StarRatings
    }
  ];
  const ratingCalulation = `${imageBaseUrl}/static/img/rate_calculation.svg`;

  const highestRating = Math.max(...(stars ?? []).map(el => +(el.percent?.split('%')?.[0] ?? 0)));

  

  useEffect(() => {

    let shortenedValue: string | any[] = [];
    let value_for_money_subrating = "";
    customerWords?.map((word, id) => {
      shortenedValue = ratingQuestionsMapping?.filter(
        product => product.question?.toLowerCase() === word.title?.toLowerCase()
      ) ?? [];   
      value_for_money_subrating =  shortenedValue?.length > 0 ? shortenedValue[0]?.answer : word.title;
    });
     
       const options = {
          root: null, // Use the viewport as the root
          rootMargin: '0px', // No margin
          threshold: 0.5 // Trigger when 20% of the slide is visible
        };
         const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
               segmentEventPush({
                event:'Ratings Widget Viewed',        
                no_of_ratings_collected : totalRatingCount,       
                products:[{
                  ...getProductItemPDP(productData)
                }],
                screen_name:'Pdp Screen',
                screen_source:'',        
                size_and_fit_subrating:'',
                value_for_money_subrating:value_for_money_subrating
              })
          }
        }, options);
      
        if(ratingRef && ratingRef.current){
           observer.observe(ratingRef.current);
        }

        return () => {        
              observer.disconnect();
        };



  }, [])

   const getStarIcon = (value: number) => {
    if (value >= 1) return `${imageBaseUrl}/static/img/black-star-one.svg`;
    if (value >= 0.6) return `${imageBaseUrl}/static/img/black-star-point-six.svg`;
    if (value >= 0.5) return `${imageBaseUrl}/static/img/black-star-point-five.svg`;
    if (value > 0) return `${imageBaseUrl}/static/img/black-star-point-four.svg`;
    return `${imageBaseUrl}/static/img/black-star-zero.svg`;
  };

  const getStar = () => {
    const stars = Array.from({ length: 5 }, (_, i) => {
      const value = Math.min(Math.max(rateData?.averageRating - i, 0), 1);
      const icon = getStarIcon(value);

      return (
        <img
          key={i}
          src={icon}
          alt={`star-${i + 1}`}
          style={{height: '16px', width: '16px'}}
        />
      );
    });
    return stars;
  }

  return (
    <>
      <div
        className={cx(
          'rating-data',
          isDesktop ? 'desk-rating-data' : 'mob-rating-data',
          isFleek ? 'fleek-mob-rating-data' : ''
        )}
        style={{ justifyContent: isDesktop && customerWords.length === 0 ? 'center' : 'flex-end' }}
        onClick={e =>{

          segmentEventPush({
            event: 'Rating Clicked',            
            page_source: 'Pdp Screen',          
            products:[{
              ...getProductItemPDP(props)
            }],
            screen_name: 'Pdp Screen' 
          })

        }}
        ref={ratingRef}
      >
        <div 
          className={cx('rateing-overview')}
          role="presentation" //even after this it says clickable with NVDA no solution
          aria-label={`${rateData?.averageRating} out of 5 star ratings by ${rateData?.numUserRatings} Customers`}
        >
          {
            ratingParityMsite && !isDesktop ? (
              <span className={cx('total-star-div')} aria-hidden='true' tabIndex={-1} style={{marginBottom: '8px'}}>
                {getStar()}
              </span>
            ) : (
              <span className={cx('total-star-div')} aria-hidden='true'>
                {rateData?.averageRating}{' '}
                {rateData?.averageRating < thresholdValue ? (
                  <img className={cx('rate-card-icon')} src={redStarUrl} alt="Star selected" />
                ) : (
                  <img className={cx('rate-card-icon')} src={starUrl} alt={starAltText} />
                )}
              </span>
            )
          }
          <div className={cx('total-rating-div')} aria-hidden='true'>
            {rateData?.numUserRatings} {isDesktop ? 'Customers' : 'Ratings'}
          </div>
        </div>
        <hr aria-hidden="true"/>
        {isProgressBar ? (
          <div className={cx('cstmr-words')}>
            <p className={cx('customer-feedback')}>{isDesktop && 'Rating Distribution'}</p>
            <ul>
              {stars &&
                stars.map((val, i) => {
                  return (
                    <li key={i}>
                      <span
                        className={cx('Excellent')}
                        aria-label={`${val.star} star, rated by ${val.percent} users`}
                      >
                        <ProgressLabel
                          height={isDesktop ? '8px' : '5px'}
                          progress={val.percent}
                          bgcolor={isDesktop ? '#d5a248' : '#1a4e7c'}
                          id={val.id}
                          ratingLabel={val.star}
                          isDesktop={isDesktop}
                          isFleek={isFleek}
                          isHighest={+(val?.percent?.split('%')?.[0] ?? 0) === highestRating}
                          hideInternalContent={true}
                          indexNum={i}
                        />
                      </span>
                    </li>
                  );
                })}
            </ul>
            {isDesktop && showRateCalculate && (
              <div className={cx('rating-calculation')} onClick={calculatePopup} aria-controls='rate-calculation-popup'>
                <span role='button' tabIndex={0} aria-expanded={showCalculatePopup} aria-controls='rate-calculation-popup'>
                  Rating Calculation <img aria-hidden="true" src={ratingCalulation} alt="Information icon" />
                </span>
                {showCalculatePopup && <RateCalculationPopup visible isDesktop={isDesktop} />}
              </div>
            )}
          </div>
        ) : (
          <div className={cx('cstmr-words')}>
            <div className={cx('Customers-Words')}>
              <strong role='heading' aria-level={4}>Customers Words</strong>
            </div>
            <div className={cx('group-word')}>
              <ul>
                {customerWords?.map((word, id) => {
                  const shortenedValue = ratingQuestionsMapping?.filter(
                    product => product.question?.toLowerCase() === word.title?.toLowerCase()
                  ) ?? [];
                  return (
                    <li key={id}>
                      <span className={cx('Product-Quality')}>
                        {shortenedValue?.length > 0 ? shortenedValue[0]?.answer : word.title}
                      </span>
                      <span className={cx('Excellent')}>{word.largestText}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default connector(RatingCard);
